import { Loader2, Pause, Play } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MessageAttachmentDto } from "@multi-chat/shared";
import { cn } from "../../../lib/utils.js";
import { DecorativeWaveform } from "../DecorativeWaveform/DecorativeWaveform.js";
import { formatMediaClock, mediaProgressRatio } from "../media-clock.js";
import { pauseHtmlAudio, playHtmlAudio, AUDIO_VISUALLY_HIDDEN_CLASS } from "../play-html-audio.js";
import { useAuthorizedAudioSrc } from "../use-authorized-audio-src.js";
import { useInboxAttachmentUrl } from "../use-inbox-attachment-url.js";
import {
  clampSeekTime,
  seekRatioFromPointer,
  voicePlayerFromPersistedDuration,
  voicePlayerOnEnded,
  voicePlayerOnError,
  voicePlayerOnLoadedMetadata,
  voicePlayerOnPlayRejected,
  voicePlayerOnTimeUpdate,
  voicePlayerTogglePlay,
} from "../voice-player-state.js";

export function MessageVoice({
  organizationId,
  attachment,
}: {
  organizationId: number;
  attachment: MessageAttachmentDto;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { urlResolving, failed } = useInboxAttachmentUrl(organizationId, attachment);
  const stored = attachment.storageState === "STORED" && !failed;
  const { playbackSrc, sourceFailed } = useAuthorizedAudioSrc(
    organizationId,
    attachment.attachmentId,
    stored,
  );
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const waveformRef = useRef<HTMLButtonElement | null>(null);
  const pendingPlayRef = useRef(false);
  const [player, setPlayer] = useState(() =>
    voicePlayerFromPersistedDuration(attachment.durationSeconds),
  );
  const isRtl = i18n.dir() === "rtl";

  useEffect(() => {
    setPlayer(voicePlayerFromPersistedDuration(attachment.durationSeconds));
  }, [attachment.attachmentId, attachment.durationSeconds]);

  useEffect(() => {
    if (!pendingPlayRef.current || !playbackSrc) {
      return;
    }
    const node = audioRef.current;
    if (!node) {
      return;
    }
    pendingPlayRef.current = false;
    void playHtmlAudio(node).then((result) => {
      if (result.status === "played") {
        return;
      }
      const errorName = result.status === "failed" ? result.errorName : "AbortError";
      setPlayer((current) => voicePlayerOnPlayRejected(current, errorName));
    });
  }, [playbackSrc]);

  const applyPlayback = useCallback((nextStatus: typeof player.status) => {
    const node = audioRef.current;
    if (nextStatus !== "playing") {
      pendingPlayRef.current = false;
      if (node) {
        pauseHtmlAudio(node);
      }
      return;
    }
    if (!node || !node.src) {
      pendingPlayRef.current = true;
      return;
    }
    void playHtmlAudio(node).then((result) => {
      if (result.status === "played") {
        return;
      }
      const errorName = result.status === "failed" ? result.errorName : "AbortError";
      setPlayer((current) => voicePlayerOnPlayRejected(current, errorName));
    });
  }, []);

  const handleToggle = useCallback(() => {
    setPlayer((current) => {
      const next =
        current.status === "error" ? voicePlayerTogglePlay({ ...current, status: "paused" }) : voicePlayerTogglePlay(current);
      queueMicrotask(() => applyPlayback(next.status));
      return next;
    });
  }, [applyPlayback]);

  const handleSeek = useCallback(
    (clientX: number) => {
      const rect = waveformRef.current?.getBoundingClientRect();
      const node = audioRef.current;
      if (!rect || !node) return;
      const ratio = seekRatioFromPointer(clientX, rect, isRtl);
      const duration = Number.isFinite(node.duration) ? node.duration : player.duration;
      const time = clampSeekTime(ratio * (duration ?? 0), duration);
      node.currentTime = time;
      setPlayer((current) => ({ ...current, currentTime: time }));
    },
    [isRtl, player.duration],
  );

  if (failed || sourceFailed) {
    return (
      <p className="m-0 text-[0.8125rem] text-ink-subtle">
        {t("inbox.media.unavailable")}
      </p>
    );
  }

  const progress = mediaProgressRatio(player.currentTime, player.duration ?? 0);
  const durationLabel =
    player.duration != null ? formatMediaClock(player.duration) : t("inbox.voice.durationPending");
  const elapsedLabel = formatMediaClock(player.currentTime);
  const isPlaying = player.status === "playing";
  const srcReady = Boolean(playbackSrc);
  const resolving = urlResolving || !srcReady;
  const canControl = srcReady;

  return (
    <div className="relative my-0.5 flex w-full max-w-full min-w-0 items-center gap-2">
      {srcReady ? (
        <audio
          ref={audioRef}
          src={playbackSrc}
          preload="metadata"
          className={AUDIO_VISUALLY_HIDDEN_CLASS}
          onLoadedMetadata={(event) => {
            const duration = event.currentTarget.duration;
            setPlayer((current) => voicePlayerOnLoadedMetadata(current, duration));
          }}
          onDurationChange={(event) => {
            const duration = event.currentTarget.duration;
            setPlayer((current) => voicePlayerOnLoadedMetadata(current, duration));
          }}
          onTimeUpdate={(event) => {
            const currentTime = event.currentTarget.currentTime;
            setPlayer((current) => voicePlayerOnTimeUpdate(current, currentTime));
          }}
          onEnded={() => setPlayer(voicePlayerOnEnded)}
          onError={() => setPlayer(voicePlayerOnError)}
        />
      ) : null}

      <button
        type="button"
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-full",
          "bg-accent text-accent-contrast shadow-sm",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
          (!canControl || resolving) && "opacity-60",
        )}
        disabled={!canControl || resolving}
        aria-label={isPlaying ? t("inbox.voice.pause") : t("inbox.voice.play")}
        onClick={handleToggle}
      >
        {resolving ? (
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        ) : isPlaying ? (
          <Pause className="size-4 fill-current" aria-hidden="true" />
        ) : (
          <Play className="size-4 translate-x-px fill-current" aria-hidden="true" />
        )}
      </button>

      <button
        ref={waveformRef}
        type="button"
        className="flex min-w-0 flex-1 flex-col gap-0.5 border-0 bg-transparent p-0 text-start"
        aria-label={t("inbox.voice.seek")}
        disabled={!canControl || player.duration == null}
        onClick={(event) => handleSeek(event.clientX)}
      >
        <DecorativeWaveform progress={progress} isRtl={isRtl} />
        <span className="flex justify-between text-[10px] tabular-nums text-ink-subtle">
          <span>{elapsedLabel}</span>
          <span>{resolving && player.duration == null ? t("inbox.media.processing") : durationLabel}</span>
        </span>
      </button>

      {player.status === "error" ? (
        <p className="absolute inset-x-0 -bottom-4 m-0 text-[10px] text-destructive">
          {t("inbox.voice.playbackFailed")}
        </p>
      ) : null}
    </div>
  );
}
